import { prepareCodesForSubmit } from "helpers/codeRanges";
import type {
  ICodeListFamily,
  IRuleBucketMemberRef,
  IRuleGroupUpdateRequest,
  TRuleGroupScope,
} from "types/AdjustmentRules/AdjustmentRules";

import { ALL_PAYERS_LABEL } from "./ruleApplicability";

/**
 * CLE-2842 — a rule *bucket*: the SCOPED rows of one type that are the same rule (same code lists,
 * same terms, same enabled state), whatever their provider, payer, name or contract language. The
 * server tags them with a shared `groupKey` and lists them, across every provider, in the by-id
 * response's `bucketMembers`.
 */

/** The fields of a MPPR / Case Rate / Package row (as loaded by id) that bucket mode reads. */
export interface IBucketableRule {
  id?: string;
  providerId?: string | null;
  provider?: { id?: string; name?: string | null } | null;
  groupKey?: string | null;
  /** The server's list of this row's bucket, across every provider, the row itself included. */
  bucketMembers?: IRuleBucketMemberRef[];
  codeListKey?: string | null;
  codeListFamily?: ICodeListFamily;
}

export interface IRuleBucketMember {
  id: string;
  providerId: string;
  providerName: string;
  /** Null on a provider's "All payers" row. */
  payerId: number | null;
  payerName: string;
  groupName: string | null;
}

/** One provider + payer the rule covers or will cover. `payerId: null` is All payers. */
export interface IBucketPair {
  providerId: string;
  providerName: string;
  payerId: number | null;
  payerName: string;
}

export interface IPayerOption {
  name: string;
  value: number | null;
}

/**
 * How far an edit reaches. "PAYER" (Only this payer) is the single-row save; "PROVIDER" (every
 * payer of this rule's provider) and "ALL" (every provider and payer of the bucket) are one atomic
 * group save.
 */
export type TBucketScope = "PAYER" | TRuleGroupScope;

export interface IBucketScopeOption {
  value: TBucketScope;
  label: string;
}

/** The code-list fields of each groupable type — what a code-list family shares. */
export const CODE_LIST_FIELDS = {
  MPPR: ["cptCodes"],
  CASE_RATE: ["cptCodes", "excludedCptCodes"],
  PACKAGE: ["triggerCptCodes", "bundledCptCodes"],
};

/** "1 rule", "3 rules" — the one count label every bucket screen uses. */
export const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

export const payerCountLabel = (count: number) => plural(count, "payer");
export const providerCountLabel = (count: number) => plural(count, "provider");
export const ruleCountLabel = (count: number) => plural(count, "rule");

/** The key of a pair, so the same provider + payer is recognised wherever it appears. */
export const pairKey = (pair: { providerId: string; payerId: number | null }) =>
  `${pair.providerId}|${pair.payerId === null ? "ALL" : pair.payerId}`;

/** What the shared bucket comparator reads from a list row or a bucket member. */
export interface IBucketSortable {
  id: string;
  providerName?: string;
  /** Explicitly null on a provider's "All payers" row; a row with no payerId at all is not one. */
  payerId?: number | string | null;
  payerName?: string;
}

/**
 * The server's member order, shared by the list (`groupRuleRows`) and the edit page: provider
 * name, then the All payers row first, then payer name, then id.
 */
export const byProviderThenPayer = (a: IBucketSortable, b: IBucketSortable) => {
  const byProvider = (a.providerName ?? "").localeCompare(b.providerName ?? "");
  if (byProvider !== 0) return byProvider;
  const byAllPayers = Number(a.payerId !== null) - Number(b.payerId !== null);
  if (byAllPayers !== 0) return byAllPayers;
  const byPayer = (a.payerName ?? "").localeCompare(b.payerName ?? "");
  if (byPayer !== 0) return byPayer;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
};

/**
 * A bucket's distinct names, longest first, ties alphabetically — so the first is its DISPLAY name
 * ("Hospital Outpatient: T codes" over "T"). The name is not part of the rule; members may differ.
 * Blank names, and the list's "-" placeholder for a rule without one, are not names.
 */
export function bucketNames(names: (string | null | undefined)[]): string[] {
  return Array.from(
    new Set(
      names
        .map((name) => (name ?? "").trim())
        .filter((name) => name && name !== "-")
    )
  ).sort((a, b) => b.length - a.length || a.localeCompare(b));
}

/** The display name of a set of members: the longest distinct name, or "" when none has one. */
export const bucketDisplayName = (names: (string | null | undefined)[]) =>
  bucketNames(names)[0] ?? "";

/**
 * Every row of `row`'s bucket, across providers, in the server's order. Empty when the row has no
 * groupKey; a lone rule is a bucket of one. A member without a provider of its own (an older
 * response) is taken to be on the loaded row's provider.
 */
export function toBucketMembers(
  row: IBucketableRule | undefined
): IRuleBucketMember[] {
  if (!row?.groupKey) return [];
  const fallbackProviderId = row.providerId ?? row.provider?.id ?? "";
  const fallbackProviderName = row.provider?.name ?? "—";
  return (row.bucketMembers ?? [])
    .map((member) => {
      const providerId = member.providerId ?? fallbackProviderId;
      return {
        id: member.id,
        providerId,
        providerName:
          member.provider?.name ??
          (providerId === fallbackProviderId ? fallbackProviderName : "—"),
        payerId: member.payerId ?? null,
        payerName:
          member.payer?.name ??
          (member.payerId == null ? ALL_PAYERS_LABEL : "—"),
        groupName: member.groupName ?? null,
      };
    })
    .sort(byProviderThenPayer);
}

/** How many rules, distinct providers and distinct payers (All payers counts as one) a set spans. */
export function summarizePairs(
  pairs: { providerId: string; payerId: number | null }[]
) {
  return {
    rules: pairs.length,
    providers: new Set(pairs.map((pair) => pair.providerId)).size,
    payers: new Set(
      pairs.map((pair) => (pair.payerId === null ? "ALL" : pair.payerId))
    ).size,
  };
}

/**
 * The scopes an edit page offers for the rule `rowId`: "Only this payer" always; "All payers of
 * <provider>" when that provider has two or more members; "All providers" when the bucket spans
 * two or more providers. Widest last.
 */
export function bucketScopeOptions(
  members: IRuleBucketMember[],
  rowId: string | undefined
): IBucketScopeOption[] {
  const self = members.find((member) => member.id === rowId) ?? members[0];
  const options: IBucketScopeOption[] = [
    { value: "PAYER", label: "Only this payer" },
  ];
  if (!self) return options;
  const providerMembers = members.filter(
    (member) => member.providerId === self.providerId
  );
  if (providerMembers.length >= 2) {
    options.push({
      value: "PROVIDER",
      label: `All payers of ${self.providerName} (${ruleCountLabel(
        providerMembers.length
      )})`,
    });
  }
  const summary = summarizePairs(members);
  if (summary.providers >= 2) {
    options.push({
      value: "ALL",
      label: `All providers (${ruleCountLabel(
        summary.rules
      )} · ${providerCountLabel(summary.providers)} · ${payerCountLabel(
        summary.payers
      )})`,
    });
  }
  return options;
}

/** The members a scope reaches: none for "Only this payer" (that is a single-row save). */
export function membersInScope(
  members: IRuleBucketMember[],
  scope: TBucketScope,
  providerId: string
): IRuleBucketMember[] {
  if (scope === "ALL") return members;
  if (scope === "PROVIDER") {
    return members.filter((member) => member.providerId === providerId);
  }
  return [];
}

/** The scope a bucket's edit page opens with: the widest it offers. */
export const widestScope = (options: IBucketScopeOption[]): TBucketScope =>
  options[options.length - 1].value;

/**
 * The Rule Name a bucket's edit page opens with: the display name of the members its default (widest)
 * scope reaches — so a save that leaves the name alone renames nobody. Null for a lone rule, which
 * keeps its own name.
 */
export function bucketOpeningName(
  row: (IBucketableRule & { groupName?: string | null }) | undefined
): string | null {
  const members = toBucketMembers(row);
  if (members.length <= 1) return null;
  const self = members.find((member) => member.id === row?.id) ?? members[0];
  const scope = widestScope(bucketScopeOptions(members, self.id));
  if (scope === "PAYER") return null;
  return bucketDisplayName(
    membersInScope(members, scope, self.providerId).map(
      (member) => member.groupName
    )
  );
}

/**
 * A code list as the form submits it: ranges expanded, then trimmed, deduped and sorted, so two
 * lists compare equal whatever their order or range notation.
 */
export function normalizeCodeList(codes: unknown): string[] {
  const list = Array.isArray(codes) ? codes.map(String) : [];
  let expanded: string[];
  try {
    expanded = prepareCodesForSubmit(list);
  } catch {
    expanded = list;
  }
  return Array.from(
    new Set(expanded.map((code) => code.trim()).filter(Boolean))
  ).sort();
}

/** Whether any of `fields` holds a different code list in `current` than in `loaded`. */
export function codeListsDiffer(
  current: Record<string, unknown>,
  loaded: Record<string, unknown>,
  fields: string[]
): boolean {
  return fields.some((field) => {
    const a = normalizeCodeList(current[field]);
    const b = normalizeCodeList(loaded[field]);
    return a.length !== b.length || a.some((code, index) => code !== b[index]);
  });
}

export function buildRuleGroupUpdateRequest<TData>({
  scope,
  scopeProviderId,
  groupKey,
  ids,
  data,
  removedIds,
  added,
  codeListKey,
  alsoUpdateCodeListFamily,
}: {
  scope: TRuleGroupScope;
  scopeProviderId: string;
  groupKey: string;
  /** Every member inside the scope, including the ones being removed. */
  ids: string[];
  data: TData;
  removedIds: string[];
  added: IBucketPair[];
  codeListKey: string | null;
  alsoUpdateCodeListFamily: boolean;
}): IRuleGroupUpdateRequest<TData> {
  return {
    ids,
    expectedGroupKey: groupKey,
    scope,
    ...(scope === "PROVIDER" ? { scopeProviderId } : {}),
    data,
    // Optional in the contract — only sent when the user actually added or removed something.
    ...(added.length
      ? {
          add: added.map(({ providerId, payerId }) => ({
            providerId,
            // Always present: the server rejects a pair without it; null is All payers.
            payerId: payerId ?? null,
          })),
        }
      : {}),
    ...(removedIds.length ? { removeIds: removedIds } : {}),
    ...(alsoUpdateCodeListFamily && codeListKey
      ? { alsoUpdateCodeListFamily: true, expectedCodeListKey: codeListKey }
      : {}),
  };
}

/** The human message an API error carries (`{ message }`, `{ messages: [] }` or a bare string). */
export function extractServerErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const { data, message } = error as any;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (Array.isArray(data.messages) && typeof data.messages[0] === "string") {
      return data.messages[0];
    }
    if (typeof data.error === "string") return data.error;
  }
  return typeof message === "string" ? message : null;
}
